import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SCUIContent } from '../../../atomic/organisms';
import { SCUIBreadCrumb } from '../../../atomic/molecules';
import { SCUIButton } from '../../../atomic/atoms';
import { Grid, TextField, Paper, Typography, Box, Divider } from '@mui/material';
import { useHistory } from 'react-router-dom';
import Swal from 'sweetalert2';
import AddIcon from '@mui/icons-material/Add';
import Helper from '../../../utils/helper';
import { JsonbuilderServiceCommand } from '../../../services/command';
import { DJBA, djbaCls } from '../dynamicJsonBuilder.classes';

const AddJsonTemplate: FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const [name, setName] = useState('');
    const [templateKey, setTemplateKey] = useState('');
    const [teamName, setTeamName] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSave = async () => {
        if (!name || !templateKey || !teamName) {
            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: t('general.fillInput'),
            });
            return;
        }

        try {
            setIsLoading(true);
            const request = {
                name,
                templateKey,
                teamName,
                createdBy: Helper.getUserName() || ''
            };

            await JsonbuilderServiceCommand.CreateTemplate(request);

            Swal.fire({
                icon: 'success',
                title: t('general.success'),
                text: t('jsonTemplateBuilder.templateCreated'),
            }).then(() => {
                history.push('/json-template');
            });
        } catch (error) {
            console.error('Error creating template:', error);
            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: t('general.somethingWentWrong'),
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        history.push('/json-template');
    };

    return (
        <SCUIContent>
            <SCUIBreadCrumb
                dontShowHome={true}
                homeIconLink={'/home'}
                onlyTitle={t('jsonTemplateBuilder.addJsonTemplate')}
                components={[
                    {
                        title: t('jsonTemplateBuilder.jsonTemplateBuilder'),
                        link: '/json-template',
                    },
                    {
                        title: t('jsonTemplateBuilder.addJsonTemplate'),
                        link: '/json-template/add-json-template',
                    }
                ]}
            />
            <div className={djbaCls('lineWrapper', DJBA.divider)} />
            <div className={DJBA.shell} style={{ flex: 1, padding: '24px' }}>
                <Paper
                    className={DJBA.paper}
                    elevation={0}
                    sx={{
                        maxWidth: '800px',
                        margin: '0 auto',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #e9ecef'
                    }}
                >
                    <Box className={DJBA.body} p={3}>
                        <Typography
                            className={DJBA.title}
                            variant="h5"
                            gutterBottom
                            sx={{
                                color: '#0288d1',
                                fontWeight: 500,
                                mb: 3
                            }}
                        >
                            {t('jsonTemplateBuilder.addJsonTemplate')}
                        </Typography>
                        <Divider sx={{ mb: 3 }} />

                        <Grid className={DJBA.fieldsGrid} container spacing={3}>
                            <Grid
                                size={{
                                    xs: 12
                                }}>
                                <TextField
                                    className={DJBA.inputTextfieldTemplateName}
                                    fullWidth
                                    label={t('jsonTemplateBuilder.templateName')}
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                    margin="normal"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: '#ffffff'
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12
                                }}>
                                <TextField
                                    className={DJBA.inputTextfieldTemplateKey}
                                    fullWidth
                                    label={t('jsonTemplateBuilder.templateKey')}
                                    value={templateKey}
                                    onChange={(e) => setTemplateKey(e.target.value)}
                                    required
                                    margin="normal"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: '#ffffff'
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12
                                }}>
                                <TextField
                                    className={DJBA.inputTextfieldTeamName}
                                    fullWidth
                                    label={t('jsonTemplateBuilder.teamName')}
                                    value={teamName}
                                    onChange={(e) => setTeamName(e.target.value)}
                                    required
                                    margin="normal"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: '#ffffff'
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <Box className={DJBA.actionsRow} sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <SCUIButton
                                className={DJBA.inputButtonCancel}
                                text={t('general.cancel')}
                                type="secondary"
                                sizeType="default"
                                size={'s'}
                                onClick={handleCancel}
                            />
                            <SCUIButton
                                className={DJBA.inputButtonSave}
                                text={t('general.save')}
                                type="primary"
                                sizeType="default"
                                size={'s'}
                                onClick={handleSave}
                                disabled={isLoading}
                                startIcon={<div style={{ color: 'white' }}><AddIcon color="inherit" /></div>}
                                showStartIcon
                            />
                        </Box>
                    </Box>
                </Paper>
            </div>
        </SCUIContent>
    );
};

export default AddJsonTemplate; 